import { useI18n } from '../../i18n';
import type { SelectOption } from '../../components/shared';
import type { ProductFormData, ProductFormErrors, ProductFormTouched, ProductModalMode, StorePriceEntry } from './types';
import { SearchableSelect } from './SearchableSelect';
import { useUploadImage } from '../../hooks/useUploadImage';
import { useMoney } from '../../providers/CurrencyProvider';

interface ProductFormModalProps {
  mode: Exclude<ProductModalMode, 'delete-confirm' | null>;
  formData: ProductFormData;
  formErrors: ProductFormErrors;
  formTouched: ProductFormTouched;
  submitError: string | null;
  isSubmitting: boolean;
  isLoadingDetail: boolean;
  imagePreview: string | null;
  categoryOptions: SelectOption[];
  supplierOptions: SelectOption[];
  storeOptions: SelectOption[];
  activeCategoryName: string;
  activeSupplierName: string;
  imageFileRef: React.RefObject<HTMLInputElement>;
  onClose: () => void;
  onFieldChange: (field: keyof ProductFormData, value: string) => void;
  onFieldBlur: (field: keyof ProductFormData) => void;
  onImagePreviewChange: (url: string | null) => void;
  onStorePricesChange: (storePrices: StorePriceEntry[]) => void;
  onSubmit: () => void;
}

const inputCls = (error?: string, touched?: boolean) =>
  `h-10 w-full rounded-lg border px-3 text-sm outline-none focus:ring-2 focus:ring-primary-200 ${
    error && touched ? 'border-red-300 bg-red-50' : 'border-slate-300 focus:border-primary-400'
  }`;

const blockNonNumeric = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault();
};

/**
 * Nhãn cho một ô nhập tiền, vd `Selling Price (₴)`.
 *
 * Bug #53: ba chuỗi dịch `sellingPriceCurrency` / `compareAtPriceCurrency` /
 * `costPriceCurrency` trước đây nhúng thẳng "($)" vào cả ba ngôn ngữ, trong khi
 * bảng danh sách sản phẩm hiển thị ₴ (UAH) — cùng một con số, hai đơn vị tiền,
 * tuỳ chỗ người dùng nhìn. Ký hiệu nay lấy từ `utils/currency` như mọi nơi khác,
 * nên đổi đơn vị tiền của hệ thống là đổi một chỗ duy nhất.
 */
const viewField = (value: string) => (
  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800">{value || '-'}</div>
);

export function ProductFormModal({
  mode, formData, formErrors, formTouched, submitError,
  isSubmitting, isLoadingDetail, imagePreview,
  categoryOptions, supplierOptions, storeOptions, activeCategoryName, activeSupplierName,
  imageFileRef, onClose, onFieldChange, onFieldBlur, onImagePreviewChange, onStorePricesChange, onSubmit,
}: ProductFormModalProps) {
  const { t } = useI18n();
  const money = useMoney();
  // moneyLabel chuyển vào trong component vì ký hiệu nay lấy từ hook `useMoney()`.
  const moneyLabel = (label: string) => `${label} (${money.symbol})`;
  const isView = mode === 'view';
  const title = mode === 'view' ? t('productDetails') : mode === 'edit' ? t('editProduct') : t('addProduct');
  const storePrices = formData.storePrices ?? [];
  const { uploadImage, isUploading, uploadError, setUploadError } = useUploadImage();

  const handleFileSelected = async (file: File) => {
    // Show local blob preview immediately — keep it for display throughout
    const blobUrl = URL.createObjectURL(file);
    onImagePreviewChange(blobUrl);
    onFieldChange('imageUrl', '');

    const fileUrl = await uploadImage(file, 'product');

    if (fileUrl) {
      // Keep blob for preview, store real URL in formData for submission
      onFieldChange('imageUrl', fileUrl);
      // Don't change imagePreview — blob still works fine for display
    } else {
      // Upload failed — clear everything
      URL.revokeObjectURL(blobUrl);
      onImagePreviewChange(null);
      onFieldChange('imageUrl', '');
      if (imageFileRef.current) imageFileRef.current.value = '';
    }
  };

  const addStorePrice = () => {
    onStorePricesChange([...storePrices, { store: '', price: '', valid_from: '', valid_to: '' }]);
  };

  const updateStorePrice = (index: number, field: keyof StorePriceEntry, value: string) => {
    const updated = storePrices.map((sp, i) => i === index ? { ...sp, [field]: value } : sp);
    onStorePricesChange(updated);
  };

  const removeStorePrice = (index: number) => {
    onStorePricesChange(storePrices.filter((_, i) => i !== index));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm" onClick={onClose}>
      <div className="flex max-h-[90vh] w-full max-w-[560px] overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex w-full flex-col">
          <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
            <h3 className="text-lg font-bold text-slate-900">{title}</h3>
            <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5">
            {isLoadingDetail && mode !== 'create' ? (
              <div className="py-8 text-center text-sm text-slate-500">{t('loading')}</div>
            ) : (
              <div className="space-y-5">

                {/* Image */}
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">{t('productImage')}</label>
                  {isView ? (
                    imagePreview
                      ? <img src={imagePreview} alt="product" className="h-32 w-32 rounded-lg object-cover border border-slate-200" />
                      : <div className="flex h-32 w-32 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-xs text-slate-400">{t('noImage')}</div>
                  ) : (
                    <div
                      className="flex w-full flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 py-6 hover:border-primary-400 hover:bg-primary-50"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const file = e.dataTransfer.files?.[0];
                        if (file?.type.startsWith('image/')) handleFileSelected(file);
                      }}
                    >
                      <input
                        ref={imageFileRef}
                        type="file"
                        accept="image/svg+xml,image/png,image/jpeg,image/gif"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleFileSelected(file);
                        }}
                      />
                      {isUploading ? (
                        <div className="flex flex-col items-center gap-2 py-4">
                          <svg className="h-8 w-8 animate-spin text-primary-500" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                          </svg>
                          <p className="text-sm text-slate-500">{t('uploading')}</p>
                        </div>
                      ) : imagePreview ? (
                        <div className="flex flex-col items-center gap-3 py-2">
                          <img src={imagePreview} alt="preview" className="h-28 w-28 rounded-lg object-cover border border-slate-200 shadow-sm" />
                          <div className="flex items-center gap-3">
                            <button type="button" onClick={() => imageFileRef.current?.click()} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">{t('changeImage')}</button>
                            <button type="button" onClick={() => { onImagePreviewChange(null); onFieldChange('imageUrl', ''); setUploadError(null); if (imageFileRef.current) imageFileRef.current.value = ''; }} className="text-xs text-red-500 hover:text-red-700">{t('remove')}</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" className="mb-3 text-slate-400">
                            <path d="M12 4v12M12 4l-4 4M12 4l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                          </svg>
                          <p className="text-sm font-semibold text-slate-700">{t('uploadFiles')}</p>
                          <p className="mt-1 text-xs text-slate-400">{t('dragAndDropUpload')}</p>
                          <button type="button" onClick={() => imageFileRef.current?.click()} className="mt-3 rounded-lg border border-slate-300 bg-white px-4 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">{t('chooseFile')}</button>
                        </>
                      )}
                      {uploadError && (
                        <p className="mt-2 text-xs text-red-600">{uploadError}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* SKU + Name */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('productCode')} {!isView && <span className="text-red-500">*</span>}</label>
                    {isView ? viewField(formData.sku) : (
                      <>
                        <input value={formData.sku} onChange={(e) => onFieldChange('sku', e.target.value)} onBlur={() => onFieldBlur('sku')} className={inputCls(formErrors.sku, formTouched.sku)} placeholder={t('enterProductCode')} />
                        {formErrors.sku && formTouched.sku && <p className="mt-1 text-xs text-red-600">{formErrors.sku}</p>}
                      </>
                    )}
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('productName')} {!isView && <span className="text-red-500">*</span>}</label>
                    {isView ? viewField(formData.name) : (
                      <>
                        <input value={formData.name} onChange={(e) => onFieldChange('name', e.target.value)} onBlur={() => onFieldBlur('name')} className={inputCls(formErrors.name, formTouched.name)} placeholder={t('enterProductName')} />
                        {formErrors.name && formTouched.name && <p className="mt-1 text-xs text-red-600">{formErrors.name}</p>}
                      </>
                    )}
                  </div>
                </div>

                {/* Barcode + Brand */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('barCode')}</label>
                    {isView ? viewField(formData.barcode) : (
                      <input value={formData.barcode} onChange={(e) => onFieldChange('barcode', e.target.value)} disabled={mode === 'edit'} className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500" placeholder={t('enterBarCode')} />
                    )}
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('brand')}</label>
                    {isView ? viewField(formData.brand) : (
                      <input value={formData.brand} onChange={(e) => onFieldChange('brand', e.target.value)} className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200" placeholder={t('enterBrand')} />
                    )}
                  </div>
                </div>

                {/* Selling Price + Compare At Price */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">{moneyLabel(t('sellingPriceCurrency'))} {!isView && <span className="text-red-500">*</span>}</label>
                    {isView ? viewField(formData.basePrice) : (
                      <>
                        <input value={formData.basePrice} type="number" min="0" step="0.01" onKeyDown={blockNonNumeric} onChange={(e) => onFieldChange('basePrice', e.target.value)} onBlur={() => onFieldBlur('basePrice')} className={inputCls(formErrors.basePrice, formTouched.basePrice)} placeholder={t('enterSellingPrice')} />
                        {formErrors.basePrice && formTouched.basePrice && <p className="mt-1 text-xs text-red-600">{formErrors.basePrice}</p>}
                      </>
                    )}
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">{moneyLabel(t('compareAtPriceCurrency'))}</label>
                    {isView ? viewField(formData.compareAtPrice) : (
                      <>
                        <input value={formData.compareAtPrice} type="number" min="0" step="0.01" onKeyDown={blockNonNumeric} onChange={(e) => { onFieldChange('compareAtPrice', e.target.value); onFieldBlur('compareAtPrice'); }} className={inputCls(formErrors.compareAtPrice, formTouched.compareAtPrice)} placeholder={t('compareAtPricePlaceholder')} />
                        {formErrors.compareAtPrice && formTouched.compareAtPrice && <p className="mt-1 text-xs text-red-600">{formErrors.compareAtPrice}</p>}
                      </>
                    )}
                  </div>
                </div>

                {/* Cost Price + Tags */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">{moneyLabel(t('costPriceCurrency'))}</label>
                    {isView ? viewField(formData.costPrice) : (
                      <input value={formData.costPrice} type="number" min="0" step="0.01" onKeyDown={blockNonNumeric} onChange={(e) => onFieldChange('costPrice', e.target.value)} className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200" placeholder={t('enterCostPrice')} />
                    )}
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('tags')}</label>
                    {isView ? viewField(formData.tags) : (
                      <input value={formData.tags} onChange={(e) => onFieldChange('tags', e.target.value)} className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200" placeholder={t('tagsPlaceholder')} />
                    )}
                  </div>
                </div>

                {/* Thuế suất VAT (sheet 2 #77).
                    Bug #52 từng gỡ cột VAT khỏi bảng sản phẩm vì lúc đó API
                    không có field nào và giá trị hiển thị là bịa. Giờ BE đã có
                    `Product.tax_rate` thật nên ô nhập này mới có nghĩa.
                    GIÁ NIÊM YẾT ĐÃ GỒM VAT — mức này để tách phần thuế ra khỏi
                    giá, KHÔNG cộng thêm vào giá. */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('taxRate')}</label>
                    {isView ? (
                      viewField(formData.taxRate ? `${formData.taxRate}%` : '')
                    ) : (
                      <>
                        <input
                          value={formData.taxRate}
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          onKeyDown={blockNonNumeric}
                          onChange={(e) => onFieldChange('taxRate', e.target.value)}
                          className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                          placeholder={t('taxRatePlaceholder')}
                        />
                        <p className="mt-1 text-xs text-slate-400">{t('taxRateHint')}</p>
                      </>
                    )}
                  </div>
                </div>

                {/* Supplier + Category */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('productsSupplier')}</label>
                    {isView ? viewField(activeSupplierName) : (
                      <SearchableSelect
                        options={supplierOptions}
                        value={formData.supplierId}
                        onChange={(v) => onFieldChange('supplierId', v)}
                        placeholder={t('selectSupplier')}
                      />
                    )}
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('productsCategory')}</label>
                    {isView ? viewField(activeCategoryName) : (
                      <SearchableSelect
                        options={categoryOptions}
                        value={formData.categoryId}
                        onChange={(v) => onFieldChange('categoryId', v)}
                        
                      />
                    )}
                  </div>
                </div>

                {/* Unit + Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('unitLabel')} {!isView && <span className="text-red-500">*</span>}</label>
                    {isView ? viewField(formData.unit) : (
                      <>
                        <input value={formData.unit} onChange={(e) => onFieldChange('unit', e.target.value)} onBlur={() => onFieldBlur('unit')} className={inputCls(formErrors.unit, formTouched.unit)} placeholder={t('unitPlaceholder')} />
                        {formErrors.unit && formTouched.unit && <p className="mt-1 text-xs text-red-600">{formErrors.unit}</p>}
                      </>
                    )}
                  </div>
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('productsStatus')}</label>
                    {isView ? viewField(formData.status === 'ACTIVE' ? t('productsActive') : t('productsInactive')) : (
                      <select value={formData.status} onChange={(e) => onFieldChange('status', e.target.value)} className="h-10 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-700 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200">
                        <option value="ACTIVE">{t('productsActive')}</option>
                        <option value="INACTIVE">{t('productsInactive')}</option>
                      </select>
                    )}
                  </div>
                </div>

                {/* Short Description */}
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('description')}</label>
                  {isView ? (
                    <div className="min-h-[72px] rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800">{formData.shortDescription || '-'}</div>
                  ) : (
                    <textarea value={formData.shortDescription} rows={3} onChange={(e) => onFieldChange('shortDescription', e.target.value)} className="w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200" placeholder={t('enterProductDescription')} />
                  )}
                </div>


                {/* Stock Tracking.
                    Ô "ngưỡng tồn tối thiểu" đã GỠ ngày 09/09/2026: ngưỡng cảnh
                    báo giờ BE tự tính (20% mốc tồn sau lần nhập gần nhất) và
                    không nhận số nhập tay nữa — PATCH /inventory/{id}/ kèm
                    `min_stock_threshold` trả 400. Ngưỡng thực tế xem ở trang
                    Inventory, không phải ở form sản phẩm. */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col justify-end">
                    <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('stockTracking')}</label>
                    {isView ? viewField(formData.stockTrackingEnabled ? 'Enabled' : 'Disabled') : (
                      <label className="flex h-10 cursor-pointer items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.stockTrackingEnabled}
                          onChange={(e) => onFieldChange('stockTrackingEnabled', String(e.target.checked))}
                          className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-200"
                        />
                        <span className="text-sm text-slate-700">{t('enableStockTracking')}</span>
                      </label>
                    )}
                  </div>
                </div>

                {/* ── Hàng hạn chế 18+ ────────────────────────────────────────
                    Nhóm này chỉ hiện khi bật "hàng 18+": ba field bên trong vô
                    nghĩa với một hộp sữa, và bày sẵn chúng ra là mời người nhập
                    điền nhầm. */}
                <div className="rounded-xl border border-slate-200 p-4">
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      disabled={isView}
                      checked={formData.isAgeRestricted}
                      onChange={(e) => onFieldChange('isAgeRestricted', String(e.target.checked))}
                      className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-200 disabled:opacity-60"
                    />
                    <span className="text-sm font-medium text-slate-700">{t('ageRestrictedProduct')}</span>
                  </label>

                  {formData.isAgeRestricted && (
                    <div className="mt-4 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="mb-1.5 block text-sm font-medium text-slate-700">
                            {t('ageRestrictionType')} {!isView && <span className="text-red-500">*</span>}
                          </label>
                          {isView ? viewField(formData.ageRestrictionType) : (
                            <>
                              <select
                                value={formData.ageRestrictionType}
                                onChange={(e) => onFieldChange('ageRestrictionType', e.target.value)}
                                onBlur={() => onFieldBlur('ageRestrictionType')}
                                className={inputCls(formErrors.ageRestrictionType, formTouched.ageRestrictionType)}
                              >
                                <option value="">{t('selectRestrictionType')}</option>
                                <option value="ALCOHOL">{t('restrictionAlcohol')}</option>
                                <option value="TOBACCO">{t('restrictionTobacco')}</option>
                                {/* Thuốc lá điện tử / túi nicotine — nhóm riêng,
                                    KHÔNG gộp vào TOBACCO vì luật khác nhau. */}
                                <option value="NICOTINE">{t('restrictionNicotine')}</option>
                                <option value="OTHER">{t('restrictionOther')}</option>
                              </select>
                              {formErrors.ageRestrictionType && formTouched.ageRestrictionType && (
                                <p className="mt-1 text-xs text-red-600">{formErrors.ageRestrictionType}</p>
                              )}
                            </>
                          )}
                        </div>
                        <div>
                          <label className="mb-1.5 block text-sm font-medium text-slate-700">{t('minAgeLabel')}</label>
                          {isView ? viewField(formData.minAge) : (
                            <>
                              <input
                                value={formData.minAge}
                                type="number"
                                min="1"
                                max="120"
                                step="1"
                                onKeyDown={blockNonNumeric}
                                onChange={(e) => onFieldChange('minAge', e.target.value)}
                                onBlur={() => onFieldBlur('minAge')}
                                className={inputCls(formErrors.minAge, formTouched.minAge)}
                                placeholder="18"
                              />
                              {formErrors.minAge && formTouched.minAge && (
                                <p className="mt-1 text-xs text-red-600">{formErrors.minAge}</p>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      {/* Chỉ rượu mới cần khai nồng độ. Bia hỏi riêng vì luật PL
                          miễn trừ bia khỏi ngưỡng 4,5% bất kể mạnh bao nhiêu —
                          không suy ra được từ con số nồng độ. */}
                      {formData.ageRestrictionType === 'ALCOHOL' && (
                        <>
                          <label className="flex cursor-pointer items-center gap-2">
                            <input
                              type="checkbox"
                              disabled={isView}
                              checked={formData.isBeer}
                              onChange={(e) => onFieldChange('isBeer', String(e.target.checked))}
                              className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-200 disabled:opacity-60"
                            />
                            <span className="text-sm text-slate-700">{t('isBeerLabel')}</span>
                          </label>

                          <div>
                            <label className="mb-1.5 block text-sm font-medium text-slate-700">
                              {t('alcoholByVolume')}{' '}
                              {!isView && !formData.isBeer && <span className="text-red-500">*</span>}
                            </label>
                            {isView ? viewField(formData.alcoholByVolume) : (
                              <>
                                <input
                                  value={formData.alcoholByVolume}
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.1"
                                  onKeyDown={blockNonNumeric}
                                  onChange={(e) => onFieldChange('alcoholByVolume', e.target.value)}
                                  onBlur={() => onFieldBlur('alcoholByVolume')}
                                  className={inputCls(formErrors.alcoholByVolume, formTouched.alcoholByVolume)}
                                  placeholder="5.2"
                                />
                                {formErrors.alcoholByVolume && formTouched.alcoholByVolume ? (
                                  <p className="mt-1 text-xs text-red-600">{formErrors.alcoholByVolume}</p>
                                ) : (
                                  // Bỏ trống KHÔNG phải 0% — backend coi chưa
                                  // khai là quá mạnh và chặn bán ở cửa hàng tự
                                  // phục vụ. Phải nói ra, không thì người nhập
                                  // tưởng để trống là "không có cồn".
                                  <p className="mt-1 text-xs text-slate-400">{t('alcoholByVolumeHint')}</p>
                                )}
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Store Prices — bug #46: hiển thị cả ở chế độ xem chi tiết (read-only),
                    không chỉ trong create/edit. */}
                {(
                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <label className="text-sm font-medium text-slate-700">{t('storePrices')}</label>
                      {!isView && (
                        <button type="button" onClick={addStorePrice} className="flex items-center gap-1 rounded-lg border border-primary-300 bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-700 hover:bg-primary-100">
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 2v8M2 6h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                          {t('addStorePriceBtn')}
                        </button>
                      )}
                    </div>
                    {storePrices.length === 0 ? (
                      <p className="text-xs text-slate-400">{t('noStorespecificPricesAdded')}</p>
                    ) : isView ? (
                      <div className="space-y-2">
                        {storePrices.map((sp, i) => {
                          const storeName = storeOptions.find((o) => o.value === sp.store)?.label ?? sp.store;
                          return (
                            <div key={i} className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm">
                              <span className="text-slate-800">{storeName}</span>
                              {/* Bug #53: trước đây in số trần, không đơn vị cũng
                                  không định dạng — cùng modal mà ô giá bán ghi
                                  "($)" còn giá theo cửa hàng thì không ghi gì. */}
                              <span className="font-semibold text-slate-900">{money.format(Number(sp.price) || 0)}</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {storePrices.map((sp, i) => (
                          <div key={i} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                            <div className="mb-2 flex items-center justify-between">
                              <span className="text-xs font-medium text-slate-600">Store Price #{i + 1}</span>
                              <button type="button" onClick={() => removeStorePrice(i)} className="text-xs text-red-500 hover:text-red-700">{t('remove')}</button>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <label className="mb-1 block text-xs text-slate-600">{t('productsStore')} <span className="text-red-500">*</span></label>
                                <SearchableSelect
                                  options={storeOptions}
                                  value={sp.store}
                                  onChange={(v) => updateStorePrice(i, 'store', v)}
                                  placeholder={t('selectStore')}
                                />
                              </div>
                              <div>
                                <label className="mb-1 block text-xs text-slate-600">{moneyLabel(t('price'))} <span className="text-red-500">*</span></label>
                                <input value={sp.price} type="number" min="0" step="0.01" onKeyDown={blockNonNumeric} onChange={(e) => updateStorePrice(i, 'price', e.target.value)} className="h-9 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200" placeholder="0.00" />
                              </div>
                              <div>
                                <label className="mb-1 block text-xs text-slate-600">{t('validFrom')}</label>
                                <input value={sp.valid_from} type="datetime-local" onChange={(e) => updateStorePrice(i, 'valid_from', e.target.value)} className="h-9 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200" />
                              </div>
                              <div>
                                <label className="mb-1 block text-xs text-slate-600">{t('validTo')}</label>
                                <input value={sp.valid_to} type="datetime-local" onChange={(e) => updateStorePrice(i, 'valid_to', e.target.value)} className="h-9 w-full rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200" />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

              </div>
            )}

            {submitError && (
              <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 whitespace-pre-line">{submitError}</div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-slate-100 px-6 py-4">
            <button onClick={onClose} className="rounded-lg px-5 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">{t('productsCancel')}</button>
            {!isView && (
              <button onClick={onSubmit} disabled={isSubmitting || isUploading} className="rounded-lg bg-primary-600 px-6 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60">
                {isSubmitting ? t('savingBtn') : isUploading ? t('uploadingBtn') : t('saveBtn')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
